'use client';

import { SettingsMaxNotificationsModalControllerBindings } from '@/components/settings-max-notifications-modal/use-settings-max-notifications-modal-controller';
import { UIPriority } from '@/enums/ui-priorities';
import Button from '@/ui/Button';
import Input from '@/ui/Input';
import { FC } from 'react';

const formatDateTime = (value: string): string =>
    new Date(value).toLocaleString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });

type SettingsMaxNotificationsContentProps = Omit<SettingsMaxNotificationsModalControllerBindings, 'closeModal'>;

export const SettingsMaxNotificationsContent: FC<SettingsMaxNotificationsContentProps> = ({
    subscriptions,
    latestLinkCode,
    intervalsBySubscriptionId,
    isLoadingSubscriptions,
    isGeneratingLinkCode,
    savingSubscriptionIds,
    onIntervalChanged,
    saveInterval,
    issueLinkCode,
    copyLinkCode,
    recipientTitleOf,
    isIntervalDraftValid,
}) => {
    return (
        <div className="space-y-6">
            <section className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        type="button"
                        priorityType={UIPriority.PRIMARY}
                        uiSize="compact"
                        className="w-52"
                        onClick={() => {
                            void issueLinkCode();
                        }}
                        disabled={isGeneratingLinkCode}
                    >
                        {isGeneratingLinkCode ? 'Создание...' : 'Создать ссылку MAX'}
                    </Button>
                    {latestLinkCode && (
                        <Button
                            type="button"
                            priorityType={UIPriority.SECONDARY}
                            uiSize="compact"
                            className="w-40"
                            onClick={() => {
                                void copyLinkCode();
                            }}
                        >
                            Скопировать ссылку
                        </Button>
                    )}
                </div>

                {latestLinkCode && (
                    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <p className="text-xs text-slate-600">
                            Ссылка действительна до: {formatDateTime(latestLinkCode.expiresAt)}
                        </p>
                        <Input readOnly value={latestLinkCode.deepLink} aria-label="MAX deep link" />
                    </div>
                )}
            </section>

            <section className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-900">Подключенные получатели</h3>

                {isLoadingSubscriptions ? (
                    <p className="text-sm text-slate-500">Загрузка подписок...</p>
                ) : subscriptions.length === 0 ? (
                    <p className="text-sm text-slate-500">
                        Пока нет подключенных получателей. Сначала откройте ссылку MAX и выполните команду `/start`.
                    </p>
                ) : (
                    <div className="space-y-3">
                        {subscriptions.map((subscription) => {
                            const intervalDraft = intervalsBySubscriptionId[subscription.id] ?? '';
                            const isSaving = savingSubscriptionIds.includes(subscription.id);
                            const intervalIsValid = isIntervalDraftValid(subscription.id);

                            return (
                                <div key={subscription.id} className="rounded-lg border border-slate-200 p-3 space-y-3">
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                        <span className="text-sm font-medium text-slate-900">
                                            {recipientTitleOf(subscription)}
                                        </span>
                                        <span className="text-xs text-slate-500">{`${subscription.recipientKind}:${subscription.recipientId}`}</span>
                                        <span
                                            className={`text-xs px-2 py-0.5 rounded-full ${
                                                subscription.isActive
                                                    ? 'bg-emerald-100 text-emerald-700'
                                                    : 'bg-slate-200 text-slate-700'
                                            }`}
                                        >
                                            {subscription.isActive ? 'Активна' : 'Неактивна'}
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap items-end gap-2">
                                        <label className="text-xs text-slate-700 flex flex-col gap-1">
                                            Интервал, минут
                                            <Input
                                                type="number"
                                                min={1}
                                                max={10080}
                                                uiSize="compact"
                                                className="w-32"
                                                value={intervalDraft}
                                                onChange={(event) =>
                                                    onIntervalChanged(subscription.id, event.target.value)
                                                }
                                            />
                                        </label>
                                        <Button
                                            type="button"
                                            priorityType={UIPriority.SECONDARY}
                                            uiSize="compact"
                                            className="w-28"
                                            onClick={() => {
                                                void saveInterval(subscription.id);
                                            }}
                                            disabled={isSaving || !intervalIsValid}
                                        >
                                            {isSaving ? 'Сохр...' : 'Сохранить'}
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
};
